import React, { useState, useEffect, useRef, useCallback } from "react";
import { Outlet, useLocation, Link } from "react-router-dom";
import Sidebar from "./Sidebar";
import ConnectionAlertBell from "../../components/layout/ConnectionAlertBell";
import { CustomerWorkspaceProvider } from "../../context/CustomerWorkspaceContext";
import { ChatWorkspaceProvider } from "../../context/ChatWorkspaceContext";
import { ConnectionHealthProvider } from "../../context/ConnectionHealthContext";
import {
  BillingStateProvider,
  useBillingState,
} from "../../features/billing/context/BillingStateContext";
import PaymentBanner from "../../features/billing/components/PaymentBanner";
import PaymentPaywall from "../../features/billing/components/PaymentPaywall";
import { useSidebarResize } from "./useSidebarResize";

const SIDEBAR_WIDTH_LS_KEY = "appSidebarWidthPx";
const SIDEBAR_WIDTH_DEFAULT = 224;
const SIDEBAR_WIDTH_MIN = 200;
const SIDEBAR_WIDTH_MAX = 400;
const SIDEBAR_COLLAPSED_WIDTH = 64;
const SIDEBAR_COLLAPSED_LS_KEY = "appSidebarCollapsed";

function readStoredSidebarWidth(): number {
  if (typeof window === "undefined") return SIDEBAR_WIDTH_DEFAULT;
  const raw = localStorage.getItem(SIDEBAR_WIDTH_LS_KEY);
  const n = raw ? parseInt(raw, 10) : NaN;
  if (!Number.isFinite(n)) return SIDEBAR_WIDTH_DEFAULT;
  return Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, n));
}

function readStoredSidebarCollapsed(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(SIDEBAR_COLLAPSED_LS_KEY) === "1";
}


/**
 * Billing gate (SGL-283). Wraps only the routed page, so a paused Brand keeps
 * its sidebar — and with it sign-out and the Brand switcher.
 *
 * `past_due` renders nothing here: the banner does that and access stays full.
 * Only `paused`, which means Stripe gave up after weeks of retries, takes the
 * page over.
 */
const BillingGate: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { showPaywall } = useBillingState();
  if (showPaywall) return <PaymentPaywall />;
  return <>{children}</>;
};


const MainLayout: React.FC = () => {
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [sidebarWidthPx, setSidebarWidthPx] = useState(readStoredSidebarWidth);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(
    readStoredSidebarCollapsed
  );
  const [isSidebarResizing, setIsSidebarResizing] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== "undefined" && window.innerWidth >= 1024
  );
  const layoutRowRef = useRef<HTMLDivElement>(null);

  const setSidebarWidthPxClamped = useCallback((value: number) => {
    setSidebarWidthPx(
      Math.min(SIDEBAR_WIDTH_MAX, Math.max(SIDEBAR_WIDTH_MIN, Math.round(value)))
    );
  }, []);

  useSidebarResize({
    isResizing: isSidebarResizing,
    layoutRowRef,
    setSidebarWidthPx: setSidebarWidthPxClamped,
    setIsResizing: setIsSidebarResizing,
    min: SIDEBAR_WIDTH_MIN,
    max: SIDEBAR_WIDTH_MAX,
  });

  useEffect(() => {
    localStorage.setItem(SIDEBAR_WIDTH_LS_KEY, String(sidebarWidthPx));
  }, [sidebarWidthPx]);

  useEffect(() => {
    localStorage.setItem(
      SIDEBAR_COLLAPSED_LS_KEY,
      isSidebarCollapsed ? "1" : "0"
    );
  }, [isSidebarCollapsed]);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const sync = () => setIsDesktop(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  // Collapse only applies on desktop; on mobile the sidebar is a slide-in drawer.
  const sidebarCollapsed = isDesktop && isSidebarCollapsed;

  const handleSidebarToggle = useCallback(() => {
    if (isDesktop) {
      setIsSidebarCollapsed((c) => !c);
    } else {
      setIsSidebarOpen(false);
    }
  }, [isDesktop]);

  const fullBleed =
    location.pathname === "/" || location.pathname === "";

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isSidebarOpen && window.innerWidth < 1024) {
        const target = event.target as HTMLElement;
        if (
          !target.closest(".sidebar") &&
          !target.closest(".hamburger-menu")
        ) {
          setIsSidebarOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isSidebarOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setIsSidebarOpen(true);
      } else {
        setIsSidebarOpen(false);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <CustomerWorkspaceProvider>
      <ChatWorkspaceProvider>
      <ConnectionHealthProvider>
      <BillingStateProvider>
      <div className="flex h-screen flex-col overflow-hidden bg-theme-background">
        {/* Mobile: single slim bar (ChatGPT-style: no duplicate desktop top nav) */}
        <header className="mobile-app-header relative flex h-12 shrink-0 items-center border-b border-theme-border bg-theme-background px-3 lg:hidden">
          <button
            type="button"
            onClick={() => setIsSidebarOpen((open) => !open)}
            className="hamburger-menu relative z-10 shrink-0 rounded-lg p-2 text-theme-fg-muted hover:bg-theme-secondary/15"
            aria-label={isSidebarOpen ? "Close menu" : "Open menu"}
          >
            <span className="material-icons-outlined text-2xl">
              {isSidebarOpen ? "close" : "menu"}
            </span>
          </button>
          <Link
            to="/"
            className="absolute left-1/2 top-1/2 flex -translate-x-1/2 -translate-y-1/2 items-center rounded-lg p-0.5 hover:opacity-90"
            aria-label="Home"
          >
            <img
              src="/sagelyn-circle.png"
              alt=""
              className="h-[2.4rem] w-[2.4rem] object-contain"
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
            />
          </Link>
          <div className="ml-auto">
            <ConnectionAlertBell />
          </div>
        </header>

        <div ref={layoutRowRef} className="relative flex min-h-0 min-w-0 flex-1">
          {isSidebarOpen && (
            <div
              className="fixed inset-x-0 bottom-0 top-12 z-40 bg-black/40 lg:hidden"
              onClick={() => setIsSidebarOpen(false)}
              aria-hidden
            />
          )}

          {/* z-40 so account + Appearance flyouts paint above main (Perplexity-style overlays). */}
          <div className="flex shrink-0 lg:relative lg:z-40">
            <div
              className={`sidebar ${
                isSidebarOpen ? "translate-x-0" : "-translate-x-full"
              } fixed bottom-0 left-0 top-12 z-50 shrink-0 ${
                isSidebarResizing
                  ? "transition-none"
                  : "transition-[transform,width] duration-300 ease-in-out"
              } lg:static lg:inset-y-0 lg:top-0 lg:z-auto lg:translate-x-0 ${
                isDesktop
                  ? "lg:min-w-0"
                  : "w-[min(14rem,88vw)] sm:w-52"
              }`}
              style={
                isDesktop
                  ? {
                      width: sidebarCollapsed
                        ? SIDEBAR_COLLAPSED_WIDTH
                        : sidebarWidthPx,
                    }
                  : undefined
              }
            >
              <Sidebar
                onClose={() => setIsSidebarOpen(false)}
                collapsed={sidebarCollapsed}
                onToggleCollapse={handleSidebarToggle}
              />
            </div>

            {!sidebarCollapsed && (
              <div
                role="separator"
                aria-orientation="vertical"
                aria-valuenow={sidebarWidthPx}
                aria-valuemin={SIDEBAR_WIDTH_MIN}
                aria-valuemax={SIDEBAR_WIDTH_MAX}
                className="hidden shrink-0 cursor-col-resize flex-col items-center justify-center bg-theme-background transition-colors hover:bg-theme-secondary/10 group lg:flex lg:w-2"
                onMouseDown={(e) => {
                  e.preventDefault();
                  setIsSidebarResizing(true);
                }}
              >
                <div className="h-10 w-0.5 rounded-full bg-theme-border group-hover:bg-theme-fg/30" />
              </div>
            )}
          </div>

          <div
            className={`relative z-0 flex min-h-0 min-w-0 flex-1 flex-col ${
              fullBleed ? "overflow-hidden" : "overflow-y-auto"
            }`}
          >
            <PaymentBanner />
            <BillingGate>
              {fullBleed ? (
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-theme-background">
                  <Outlet />
                </div>
              ) : (
                <div className="flex-1 bg-theme-background p-4 sm:p-6">
                  <Outlet />
                </div>
              )}
            </BillingGate>
          </div>
        </div>
      </div>
      </BillingStateProvider>
      </ConnectionHealthProvider>
      </ChatWorkspaceProvider>
    </CustomerWorkspaceProvider>
  );
};

export default MainLayout;
